import { Fragment } from "preact";
import Link, {
  ProductLink,
  StickyButtons,
  affiliateMerchants,
} from "../../../../components/link";
import AffiliateDisclosure from "../../../../components/affiliate-disclosure";
import Center from "../../../../components/center";
import Editorial from "../../../../components/editorial";
import Head from "../../../../components/head";
import Hero from "../../../../components/hero";
import Tags from "../../../../components/tags";
import Author from "../../../../components/author";
import SlugHeading from "../../../../components/slug-heading";
import PhotoListItem from "../../../../components/photo-list-item";
import ReviewItem from "../../../../components/review-item";
import Questions from "../../../../components/questions";
import { lookupByUrl } from "../../../../settings/route-data-lookup";
import namedRoutes from "../../../../settings/routes";

const headerInfo = lookupByUrl(
  namedRoutes.ecoflow.deltaProUltra.xt60Compatibility
);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      thumbnail={headerInfo?.og_image}
      titlePrefix="EcoFlow"
      title="DELTA Pro Ultra XT60 Compatibility"
      subTitle="Alternator Chargers & Other Accessories"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          Due to port and polarity differences, connecting an EcoFlow alternator
          charger or third-party accessories to the{" "}
          <strong>EcoFlow DELTA Pro Ultra</strong> often requires custom
          adapters. Below is a breakdown of how to safely make an{" "}
          <strong>XT60 to MC4 adapter</strong> for your system.
        </p>

        <AffiliateDisclosure />
      </Center>

      <Center>
        <SlugHeading tag="h2">Making an XT60 to MC4 Adapter</SlugHeading>
        <p>
          Unfortunately, I am unable to find an off-the-shelf cable online that
          provides both the correct polarity and a proper cable gauge (10AWG or
          thicker). However, you can easily build your own using the tools and
          materials below.
        </p>
        <p class="caption-left">
          <strong>Note:</strong> This method doesn't work for the newer{" "}
          <ProductLink brand="EcoFlow" model="DELTA Pro Ultra X">
            DELTA Pro Ultra X
          </ProductLink>
          , as it uses a different LOW-PV port interface and has different input
          ratings.
        </p>

        <h3>Required Materials & Tools:</h3>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B02MacrMW"
              href="https://www.amazon.com/dp/B0BBR63BFT/"
              img="/images/products/assorted/cables/xt60-extension.webp"
              title="XT60 Extension Cable"
              titleSuffix="(10AWG)"
              description={
                <Fragment>
                  Ensures adequate wire gauge for high-current transfer.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B06WbYgRf"
              href="https://www.amazon.com/dp/B073TX1N5Q/"
              img="/images/products/bougerv-mc4-10awg-12pc.webp"
              title="MC4 Connector Kit"
              titleSuffix="(10AWG)"
              description={
                <Fragment>
                  Standard solar male and female terminals sized for 10AWG wire.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
          <li>
            <PhotoListItem
              affiliate="https://link.amazon/B00aWmPAa"
              href="https://www.amazon.com/dp/B00OMNT17E/"
              img="/images/products/assorted/tools/mc4-crimper.webp"
              title="MC4 Cable Crimping Tool"
              description={
                <Fragment>
                  Essential for securely crimping male and female solar
                  terminals onto bare wire ends.
                </Fragment>
              }
              buttonText="Amazon"
              buttonIcon="Amazon"
            />
          </li>
        </ul>

        <h3>Step-by-Step Assembly</h3>
        <ol>
          <li>
            <strong>Cut the Extension Cable:</strong> Cut the 10AWG XT60
            extension cable to your desired length. Make sure you cut off the
            correct end for your setup.{" "}
            <p class="caption-left">
              Tip: If you cut the cable near the middle, save the remaining half
              for future DIY projects.
            </p>
          </li>
          <li>
            <strong>Strip the Wires:</strong> Strip the outer insulation on both
            the positive (red) and negative (black) cables to expose the bare
            wires.
          </li>
          <li>
            <strong>Match Connector Polarity:</strong> Crimp the appropriate
            male and female MC4 connectors onto the wires so that red connects
            to red and black connects to black when plugged into the Solar to{" "}
            <Link
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-to-low-pv-port-charging-cable?variant=40768210272329"
            >
              Solar to Low-PV Port Charging Cable
            </Link>
            .
          </li>
          <li>
            <strong>Crimp the Terminals:</strong> Using the crimping tool, crimp
            the MC4 terminals onto the exposed copper leads and assemble the
            connector housings.
          </li>
        </ol>

        <h3>Connecting to the LOW-PV Port</h3>
        <p>
          Once your custom cable is assembled, plug the MC4 ends directly into
          the{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/solar-to-low-pv-port-charging-cable?variant=40768210272329"
          >
            Solar to Low-PV Port Charging Cable
          </Link>{" "}
          that came in the box with your{" "}
          <ProductLink brand="EcoFlow" model="DELTA Pro Ultra">
            DELTA Pro Ultra
          </ProductLink>{" "}
          power station.
        </p>
        <p class="caption-left">
          If you misplaced this cable, a replacement{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/solar-to-low-pv-port-charging-cable?variant=40768210272329"
          >
            Solar to Low-PV Port Charging Cable
          </Link>{" "}
          can be ordered directly from EcoFlow.
        </p>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-ultra?variant=40758830071881"
              img="/images/products/ecoflow/delta-pro-ultra/front-right.webp"
              title="EcoFlow DELTA Pro Ultra"
              description={<Fragment>Whole-Home Backup Power</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-to-low-pv-port-charging-cable?variant=40768210272329"
              img="/images/products/ecoflow/accessories/solar-to-low-pv-port-charging-cable.webp"
              title="Solar to Low-PV Port Charging Cable"
              titleSuffix="(14AWG)"
              description={
                <Fragment>
                  Connect a solar panel or other DC input to EcoFlow DELTA Pro
                  Ultra.
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <h3>EcoFlow Alternator Charger XT60 Output Cables</h3>
        <p>
          If using with an EcoFlow alternator charger, ensure you have the
          EcoFlow Alternator Charger XT60 Output Cable to run power from the
          alternator charger into your custom XT60/MC4 cable.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m"
              img="/images/products/ecoflow/alternator-charger-500w/output-cable-xt60i.webp"
              title="XT60 output cable"
              description={
                <Fragment>
                  Compatible with 500W & 800W Alternator Chargers
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-xt60output"
              img="/images/products/ecoflow/alternator-charger-600/xt60-cable.webp"
              title="XT60 output cable"
              description={
                <Fragment>
                  Compatible with 600W & 1000W Alternator Chargers
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <h3>Alternator Charger Compatibility</h3>
        <ul class="none">
          <li>
            <ReviewItem
              href={namedRoutes.ecoflow.altCharger.gen1Compatibility}
            />
          </li>
          <li>
            <ReviewItem
              href={namedRoutes.ecoflow.altCharger.gen2Compatibility}
            />
          </li>
        </ul>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>

      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="Fuse Block + Cable"
        description="Anderson & Cigarette Options"
        options={[
          {
            text: "View Products",
            icon: "Amazon",
            // secondary: true,
            options: [
              {
                text: "Fuse Block + Anderson Powerpole Cable",
                affiliate: "https://link.amazon/B04cMwcvd",
                href: "https://www.amazon.com/shop/matanich/photo/amzn1.shoppablemedia.v1.collage.0296f7f0-f7d2-40b2-a91d-e9e759b9c193?tag=matanich-20",
              },
              {
                text: "Fuse Block + Cigarette Port Cable",
                affiliate: "https://link.amazon/B0hcWH54y",
                href: "https://www.amazon.com/shop/matanich/photo/amzn1.shoppablemedia.v1.collage.f0e7827a-0e8d-45e0-8829-a6752c421c95?tag=matanich-20",
              },
            ],
          },
        ]}
      />
    </Editorial>
  </div>
);

export default Page;
